import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebookF,
  faInstagram,
  faLinkedinIn,
  faTiktok,
} from "@fortawesome/free-brands-svg-icons";
import { subscribeMarketingNewsletter } from "../../../api/SubscribeNewsletter";
import { validateEmail } from "../Utils/ValidateEmail";
import { errorHandler } from "../Utils/ErrorHandler";
import { lineBreaks } from "../Utils/LineBreaks";
import classes from "./Footer.module.css";

const Footer = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    if (emailError) setEmailError("");
  };

  const subscribeToNewsletter = async (email) => {
    if (!validateEmail(email)) {
      throw new Error("Please enter a valid email address.");
    }

    const payload = {
      email: email,
      active: false,
    };

    try {
      await subscribeMarketingNewsletter(payload);
      navigate("/");
      setEmail("");
      setEmailError("");
    } catch (error) {
      let errorMessage = errorHandler(error);
      if (errorMessage.includes("email:")) {
        errorMessage = errorMessage.substring(7, errorMessage.length - 1);
      }
      const errorMessageWithBreaks = lineBreaks(errorMessage);

      setEmailError(errorMessageWithBreaks);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (email.trim() === "") {
      setEmailError("Please enter an email address.");
      return;
    }
    subscribeToNewsletter(email);
  };

  const handleClick = (event) => {
    const path = event.currentTarget.getAttribute("to");
    window.scrollTo(0, 0);
    navigate(path);
  };

  return (
    <footer className={classes.footer_container}>
      <div className={classes.footer_content}>
        {/* Sponsor a Pet Column */}
        <div className={classes.footer_column}>
          <h2 className="font-bold text-xl mb-4">Sponsor a Pet</h2>
          <p className="text-gray-200 mb-6 max-w-xs">
            Sponsor a Pet, the happy fundraising platform, empowers Animal
            Non-Profits to share their stories and secure the vital resources
            needed to save animals' lives.
          </p>
          <div className="flex space-x-4 mb-6">
            <a
              href="https://www.linkedin.com/company/sponsor-a-pet"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="text-white hover:text-gray-300 transition"
            >
              <FontAwesomeIcon icon={faLinkedinIn} size="lg" />
            </a>
            <a
              href="https://www.tiktok.com/@sponsorapet"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
              className="text-white hover:text-gray-300 transition"
            >
              <FontAwesomeIcon icon={faTiktok} size="lg" />
            </a>
            <a
              href="https://www.facebook.com/sponsorapetfoundation/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="text-white hover:text-gray-300 transition"
            >
              <FontAwesomeIcon icon={faFacebookF} size="lg" />
            </a>
            <a
              href="https://www.instagram.com/sponsorapet/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="text-white hover:text-gray-300 transition"
            >
              <FontAwesomeIcon icon={faInstagram} size="lg" />
            </a>
          </div>
          <Link
            className={classes.read_more_btn}
            to="/about-us"
            onClick={handleClick}
          >
            Read More
          </Link>
        </div>

        {/* About Column */}
        <div className={classes.footer_column}>
          <h2 className="font-bold text-xl mb-4">About</h2>
          <ul>
            <li>
              <Link
                className="no-underline text-gray-200 hover:text-white transition"
                to="/about-us"
                onClick={handleClick}
              >
                Our Mission
              </Link>
            </li>
            <li>
              <Link
                className="no-underline text-gray-200 hover:text-white transition"
                to="/faq"
                onClick={handleClick}
              >
                FAQ
              </Link>
            </li>
            <li>
              <Link
                className="no-underline text-gray-200 hover:text-white transition"
                to="/contact-us"
                onClick={handleClick}
              >
                Contact
              </Link>
            </li>
            <li>
              <Link
                className="no-underline text-gray-200 hover:text-white transition"
                to="/careers"
                onClick={handleClick}
              >
                Careers
              </Link>
            </li>
          </ul>
        </div>

        {/* Legal Column */}
        <div className={classes.footer_column}>
          <h2 className="font-bold text-xl mb-4">Legal</h2>
          <ul>
            <li>
              <Link
                className="no-underline text-gray-200 hover:text-white transition"
                to="/terms-of-service"
                onClick={handleClick}
              >
                Terms of Service
              </Link>
            </li>
            <li>
              <Link
                className="no-underline text-gray-200 hover:text-white transition"
                to="/refund-policy"
                onClick={handleClick}
              >
                Refund Policy
              </Link>
            </li>
            <li>
              <Link
                className="no-underline text-gray-200 hover:text-white transition"
                to="/privacy-policy"
                onClick={handleClick}
              >
                Privacy Policy
              </Link>
            </li>
          </ul>
        </div>

        {/* Newsletter Column */}
        <div className={classes.footer_column}>
          <h2 className="font-bold text-xl mb-4">Newsletter</h2>
          <p className="text-gray-200 mb-4">Subscribe for News & Updates</p>
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <input
              type="email"
              id="newsletter-email"
              placeholder="Email"
              value={email}
              onChange={handleEmailChange}
              className={classes.email_input}
            />
            {emailError && (
              <p className="text-red-300 font-medium text-sm">{emailError}</p>
            )}
            <button className={classes.subscribe_btn} type="submit">
              Subscribe
            </button>
          </form>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
